import { html } from 'htm/preact';
import { Fragment } from 'preact';
import { useEffect } from 'preact/hooks';
import { LocationProvider, Router, lazy, useLocation } from 'preact-iso';
import { firstRefreshed, user } from '../store/auth.js';
import { previousRoute } from '../store/redirect.js';
import { swControllerVersion, initWorkbox } from '../store/wb.js';
import { enableVideoCaching, disableVideoCaching, refreshCachedVideos } from '../store/cache.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { adminLazy, adminLazyNamed } from '../utils/adminLazy.js';
import { userLazy } from '../utils/userLazy.js';
import { Redirect } from './Redirect.js';
import { OfflineWatcher } from './OfflineWatcher.js';

// useLocation only work inside LocationProvider component
function RedirectUpdater() {
  const { url } = useLocation();

  const EXCLUDED_ROUTES = ['/login', '/logout', '/reset-password', '/403'];

  function isExcluded(url) {
    const path = url.split('?')[0] ?? '';
    return EXCLUDED_ROUTES.some((r) => path === r || path.startsWith(r + '/'));
  }

  useEffect(() => {
    if (!isExcluded(url)) previousRoute.value = url;
  }, [url]);
}

export function App() {
  // Don't waste first render on swReady : SW activation and offline features
  // will be activated alone.
  const isLoading = !firstRefreshed.value;

  useEffect(() => {
    if (!isLoading) document.getElementById('boot-loader')?.remove();
  }, [isLoading]);

  useEffect(() => {
    const init = async () => {
      await initWorkbox();

      if (user.isLogged.value) {
        enableVideoCaching();
        await refreshCachedVideos();
      } else {
        disableVideoCaching();
      }
    };

    init();
    // Re-run when a new service worker takes control mid-session
  }, [user.isLogged.value, swControllerVersion.value]);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Remove old service worker (v1)
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((reg) => {
          if (reg.active?.scriptURL.includes('videoserviceworker.js')) {
            reg.unregister();
          }
        });
      });
    }
  }, []);

  return isLoading
    ? null
    : html`
        <${Fragment}>
          <${OfflineWatcher} />
          <${ConfirmDialog} />
          <${LocationProvider}>
            <${RedirectUpdater} />
            <${Router}>
              <${lazy(() => import('../pages/Home.js'))} path="/" />
              <${lazy(() => import('../pages/Login.js'))} path="/login" />
              <${userLazy(() => import('../pages/Bookmarks.js'))} path="/bookmarks" />
              <${userLazy(() => import('../pages/Downloads.js'))} path="/downloads" />
              <${userLazy(() => import('../pages/UserAccount.js'))} path="/user-account" />
              <${userLazy(() => import('../pages/Logout.js'))} path="/logout" />
              <${lazy(() => import('../pages/ResetPassword.js'))} path="/reset-password" />
              <${lazy(() => import('../pages/ResetPassword.js'))} path="/reset-password/:username" />
              <${Redirect} path="/watch-video/:videoId" to="/video/:videoId" />
              <${lazy(() => import('../pages/WatchVideo.js'))} path="/video/:videoId" />
              <${lazy(() => import('../pages/Forbidden.js'))} path="/403" />
              <${lazy(() => import('../pages/NotFound.js'))} default />
              <${adminLazy(() => import('../pages/AdminDashboard.js'))} path="/admin" />
              <${adminLazy(() => import('../pages/AdminVideoList.js'))} path="/admin/video-list" />
              <${adminLazy(() => import('../pages/AdminNewVideo.js'))} path="/admin/new-video" />
              <${adminLazy(() => import('../pages/AdminVideoSettings.js'))} path="/admin/video-settings/:videoId" />
              <${adminLazy(() => import('../pages/AdminUserList.js'))} path="/admin/user-list" />
              <${adminLazyNamed(() => import('../pages/AdminNewUser.js'), 'AdminNewUser')} path="/admin/new-user" />
              <${adminLazyNamed(() => import('../pages/AdminNewUser.js'), 'AdminNewAdmin')} path="/admin/new-admin" />
              <${adminLazyNamed(() => import('../pages/AdminUserSettings.js'), 'AdminUserSettings')} path="/admin/user-settings/:userId" />
              <${adminLazyNamed(() => import('../pages/AdminUserSettings.js'), 'AdminAdminSettings')} path="/admin/admin-settings/:adminId" />
              <${adminLazy(() => import('../pages/AdminAdminList.js'))} path="/admin/admin-list" />
              <${adminLazy(() => import('../pages/AdminGroupList.js'))} path="/admin/group-list" />
              <${adminLazy(() => import('../pages/AdminNewGroup.js'))} path="/admin/new-group" />
              <${adminLazy(() => import('../pages/AdminGroupSettings.js'))} path="/admin/group-settings/:groupId" />
            <//>
          <//>
        <//>
      `;
}
